import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { api } from '../contexts/ApiContext';
import {
  buildPropertyIncomeYearRows,
  buildYearMonthRows,
  buildStackedPlRows,
  categoriesInSection,
  CURRENT_YEAR_COLOR,
  deltaVsPriorYear,
  hasAnyAmount,
  pointsForSection,
  propertyColumnColor,
  propertyIncomeHasAmount,
  sortPropertyNicknames,
  stackedPlHasAmount,
  STACKED_PL_COLORS,
  stackedPlKey,
  yearColor,
  yearMonthRowsForColumns,
  ytdTotalsByYear,
  type AmountMode,
  type ChartMode,
  type PerformancePoint,
  type PerformanceSectionMeta,
  type PropertyIncomePoint,
  type PropertyYearRow,
  type StackedPlRow,
  type YearMonthRow,
} from '../utils/annualPerformanceCharts';

type RegionFilter = 'ire' | 'uk';

type PerformanceSeries = {
  as_of: string;
  current_year: number;
  current_month: number;
  years: number[];
  sections: PerformanceSectionMeta[];
  regions: {
    ire?: RegionSeries;
    uk?: RegionSeries;
  };
};

type RegionSeries = {
  currency: string;
  gbp_converted?: boolean;
  gbp_to_eur_rate?: number | null;
  tx_count: number;
  points: PerformancePoint[];
  properties?: Array<{ id: number; nickname: string }>;
  property_income?: PropertyIncomePoint[];
};

const SECTION_AMOUNT_MODE: Record<string, AmountMode> = {
  income: 'gross',
  expenses: 'gross',
  needs: 'gross',
  internal: 'signed',
  financing: 'signed',
  capital: 'signed',
  tax: 'signed',
  reversal: 'signed',
};

function formatMoneyWhole(amount?: number | null, currency = 'EUR'): string {
  const value = Math.round(Number(amount || 0));
  const ccy = String(currency || 'EUR').trim().toUpperCase() || 'EUR';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: ccy,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value} ${ccy}`;
  }
}

function formatChartTickWhole(v: number): string {
  const n = Math.round(v);
  const abs = Math.abs(n);
  if (abs >= 1000) return `${n < 0 ? '-' : ''}${Math.round(abs / 1000)}k`;
  return String(n);
}

function signedDeltaColor(n: number, invert = false): 'success.main' | 'error.main' | 'text.secondary' {
  const value = invert ? -n : n;
  if (value > 0) return 'success.main';
  if (value < 0) return 'error.main';
  return 'text.secondary';
}

const PerformanceSeriesChart: React.FC<{
  rows: YearMonthRow[];
  years: number[];
  currentYear: number;
  currency: string;
  height?: number;
}> = ({ rows, years, currentYear, currency, height = 280 }) => {
  const moneyTip = (value: number | string, name: string): [string, string] => [
    typeof value === 'number' ? formatMoneyWhole(value, currency) : String(value),
    name,
  ];
  const chartRows = yearMonthRowsForColumns(rows, years);
  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartRows} margin={{ top: 12, right: 16, left: 8, bottom: 8 }} barCategoryGap="18%" barGap={2}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" interval={0} tick={{ fontSize: 12 }} />
          <YAxis width={56} tickFormatter={formatChartTickWhole} />
          <RechartsTooltip formatter={moneyTip} />
          <Legend />
          {years.map((year) => (
            <Bar
              key={year}
              dataKey={String(year)}
              fill={yearColor(year, years, currentYear)}
              maxBarSize={28}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const PerformanceStackedPlChart: React.FC<{
  rows: StackedPlRow[];
  years: number[];
  currency: string;
  height?: number;
}> = ({ rows, years, currency, height = 300 }) => {
  const sortedYears = useMemo(() => [...years].sort((a, b) => a - b), [years]);
  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 12, right: 16, left: 8, bottom: 8 }} barCategoryGap="18%" barGap={2}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" interval={0} tick={{ fontSize: 12 }} />
          <YAxis width={56} tickFormatter={formatChartTickWhole} />
          <ReferenceLine y={0} stroke="#94A3B8" />
          <RechartsTooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0]?.payload as StackedPlRow | undefined;
              if (!row) return null;
              return (
                <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', p: 1, borderRadius: 1 }}>
                  <Typography variant="caption" display="block" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {String(label)}
                  </Typography>
                  {sortedYears.map((year) => {
                    const income = row[stackedPlKey(year, 'income')];
                    const expenses = row[stackedPlKey(year, 'expenses')];
                    const profit = row[stackedPlKey(year, 'profit')];
                    if (income == null && expenses == null && profit == null) return null;
                    return (
                      <Typography key={year} variant="body2">
                        {year}: Inc {formatMoneyWhole(Number(income || 0), currency)} · Exp{' '}
                        {formatMoneyWhole(Math.abs(Number(expenses || 0)), currency)} · P/L{' '}
                        {formatMoneyWhole(Number(profit || 0), currency)}
                      </Typography>
                    );
                  })}
                </Box>
              );
            }}
          />
          <Legend />
          {sortedYears.flatMap((year, idx) => [
            <Bar
              key={`${year}-income`}
              dataKey={stackedPlKey(year, 'income')}
              name="Income"
              fill={STACKED_PL_COLORS.income}
              legendType={idx === 0 ? 'rect' : 'none'}
              maxBarSize={28}
              isAnimationActive={false}
            />,
            <Bar
              key={`${year}-expenses`}
              dataKey={stackedPlKey(year, 'expenses')}
              name="Expenses"
              fill={STACKED_PL_COLORS.expenses}
              legendType={idx === 0 ? 'rect' : 'none'}
              maxBarSize={28}
              isAnimationActive={false}
            />,
            <Bar
              key={`${year}-profit`}
              dataKey={stackedPlKey(year, 'profit')}
              name="Profit"
              fill={STACKED_PL_COLORS.profit}
              legendType={idx === 0 ? 'rect' : 'none'}
              maxBarSize={28}
              isAnimationActive={false}
            />,
          ])}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const PerformancePropertyIncomeChart: React.FC<{
  rows: PropertyYearRow[];
  nicknames: string[];
  currency: string;
  height?: number;
}> = ({ rows, nicknames, currency, height = 280 }) => {
  const moneyTip = (value: number | string, name: string): [string, string] => [
    typeof value === 'number' ? formatMoneyWhole(value, currency) : String(value),
    name,
  ];
  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 12, right: 16, left: 8, bottom: 8 }} barCategoryGap="18%" barGap={2}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" interval={0} tick={{ fontSize: 12 }} />
          <YAxis width={56} tickFormatter={formatChartTickWhole} />
          <RechartsTooltip formatter={moneyTip} />
          <Legend />
          {nicknames.map((nick) => (
            <Bar
              key={nick}
              dataKey={nick}
              fill={propertyColumnColor(nick, nicknames)}
              maxBarSize={36}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const Performance: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const regionParam = String(searchParams.get('region') || '').trim().toLowerCase();
  const regionFilter: RegionFilter = regionParam === 'uk' ? 'uk' : 'ire';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [series, setSeries] = useState<PerformanceSeries | null>(null);
  const [chartMode, setChartMode] = useState<ChartMode>('cumulative');
  const [yearsBack, setYearsBack] = useState(6);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.get('/api/annual-performance/series', {
          params: { years_back: yearsBack, committed: 'all' },
        });
        if (!cancelled) setSeries(res.data as PerformanceSeries);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || 'Failed to load performance series');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [yearsBack]);

  const currentYear = series?.current_year || new Date().getFullYear();
  const currentMonth = series?.current_month || new Date().getMonth() + 1;
  const years = useMemo(() => (Array.isArray(series?.years) ? series.years : []), [series]);
  const sections = useMemo(
    () => (Array.isArray(series?.sections) ? series.sections : []),
    [series]
  );
  const region = series?.regions?.[regionFilter];
  const currency = region?.currency || (regionFilter === 'uk' ? 'GBP' : 'EUR');
  const points = useMemo(() => region?.points || [], [region?.points]);
  const yearsWithData = useMemo(() => {
    const present = new Set(points.map((point) => point.year));
    present.add(currentYear);
    return years.filter((year) => present.has(year));
  }, [years, points, currentYear]);
  const ytd = useMemo(
    () => ytdTotalsByYear(points, years, currentMonth),
    [points, years, currentMonth]
  );
  const thisYearYtd = ytd.find((row) => row.year === currentYear);
  const lastYearYtd = ytd.find((row) => row.year === currentYear - 1);
  const delta = deltaVsPriorYear(thisYearYtd, lastYearYtd);
  const monthLabel = new Date(currentYear, currentMonth - 1, 1).toLocaleString('en-IE', {
    month: 'long',
  });

  const overview = useMemo(() => {
    const incomeRows = buildYearMonthRows(pointsForSection(points, 'income'), yearsWithData, {
      mode: chartMode,
      amountMode: 'gross',
      throughMonth: currentMonth,
      currentYear,
    });
    const expenseRows = buildYearMonthRows(pointsForSection(points, 'expenses'), yearsWithData, {
      mode: chartMode,
      amountMode: 'gross',
      throughMonth: currentMonth,
      currentYear,
    });
    const netRows: YearMonthRow[] = incomeRows.map((row, idx) => {
      const exp = expenseRows[idx];
      const next: YearMonthRow = { month: row.month, monthNum: row.monthNum };
      yearsWithData.forEach((year) => {
        const key = String(year);
        const incomeVal = row[key];
        const expenseVal = exp?.[key];
        if (incomeVal == null && expenseVal == null) {
          next[key] = null;
          return;
        }
        next[key] = Number(incomeVal || 0) - Number(expenseVal || 0);
      });
      return next;
    });
    return {
      charts: [
        { key: 'net', title: 'Profit / Loss', rows: netRows },
        { key: 'income', title: 'Income', rows: incomeRows },
        { key: 'expenses', title: 'Expenses', rows: expenseRows },
      ],
      stackedRows: buildStackedPlRows(incomeRows, expenseRows, netRows, yearsWithData),
    };
  }, [points, yearsWithData, chartMode, currentMonth, currentYear]);
  const overviewCharts = overview.charts;
  const stackedPlRows = overview.stackedRows;
  const propertyNicknames = useMemo(() => {
    const fromMeta = (region?.properties || []).map((p) => p.nickname);
    const fromPoints = (region?.property_income || []).map((p) => p.nickname);
    return sortPropertyNicknames(Array.from(new Set([...fromMeta, ...fromPoints].filter(Boolean))));
  }, [region?.properties, region?.property_income]);
  const propertyIncomeRows = useMemo(
    () =>
      buildPropertyIncomeYearRows(region?.property_income || [], yearsWithData, propertyNicknames),
    [region?.property_income, yearsWithData, propertyNicknames]
  );

  const regionLabel = regionFilter === 'uk' ? 'UK' : 'IE';

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'flex-end' }}
          justifyContent="space-between"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="h4">Performance</Typography>
            <Typography variant="body2" color="text.secondary">
              Year-over-year category totals using the same P&L classes as{' '}
              <MuiLink component={RouterLink} to="/annual-returns">
                Annual Returns
              </MuiLink>
              . The X-axis always shows January–December. This year’s series stop at {monthLabel}; prior years
              show the full calendar year. Summary cards compare the same months year over year.
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ maxWidth: '100%' }}>
          <CardContent sx={{ px: { xs: 1, sm: 2 }, '&:last-child': { pb: 2 } }}>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 20,
                bgcolor: 'background.paper',
                mx: { xs: -1, sm: -2 },
                px: { xs: 1, sm: 2 },
                pt: 0.5,
                pb: 1,
                mb: 2,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Tabs
                value={regionFilter}
                onChange={(_, value: RegionFilter) => {
                  const next = new URLSearchParams(searchParams);
                  next.set('region', value);
                  setSearchParams(next, { replace: true });
                }}
                sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab value="ire" label={`IE (${series?.regions?.ire?.tx_count ?? 0})`} />
                <Tab value="uk" label={`UK (${series?.regions?.uk?.tx_count ?? 0})`} />
              </Tabs>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={chartMode}
                  onChange={(_, value: ChartMode | null) => {
                    if (value) setChartMode(value);
                  }}
                >
                  <ToggleButton value="cumulative">Cumulative YTD</ToggleButton>
                  <ToggleButton value="monthly">Monthly</ToggleButton>
                </ToggleButtonGroup>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="performance-years-back">Years</InputLabel>
                  <Select
                    labelId="performance-years-back"
                    label="Years"
                    value={String(yearsBack)}
                    onChange={(event) => setYearsBack(Number(event.target.value))}
                  >
                    {[3, 4, 5, 6, 8, 10].map((n) => (
                      <MenuItem key={n} value={String(n)}>
                        Last {n} years
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Income by property
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Same P&L Income as Annual Returns, grouped by the property tag on each transaction.
                    This year is year-to-date. Untagged income is Unassigned; a TX tagged to more than one
                    property is split evenly.
                  </Typography>
                  {propertyIncomeHasAmount(propertyIncomeRows, propertyNicknames) ? (
                    <PerformancePropertyIncomeChart
                      rows={propertyIncomeRows}
                      nicknames={propertyNicknames}
                      currency={currency}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No property income in this range.
                    </Typography>
                  )}
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  So far this year · {regionLabel} {currency}
                  {region?.gbp_converted && region.gbp_to_eur_rate
                    ? ` · GBP converted at ${region.gbp_to_eur_rate}`
                    : ''}
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  {(
                    [
                      { label: 'Income', value: thisYearYtd?.income, delta: delta.income, invert: false },
                      { label: 'Expenses', value: thisYearYtd?.expenses, delta: delta.expenses, invert: true },
                      { label: 'Profit / Loss', value: thisYearYtd?.net, delta: delta.net, invert: false },
                    ] as const
                  ).map((card) => (
                    <Card key={card.label} variant="outlined" sx={{ flex: 1 }}>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          {card.label}
                        </Typography>
                        <Typography variant="h5" sx={{ color: card.label === 'Profit / Loss' ? CURRENT_YEAR_COLOR : 'text.primary' }}>
                          {formatMoneyWhole(card.value, currency)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: signedDeltaColor(card.delta, card.invert) }}>
                          {card.delta > 0 ? '+' : ''}
                          {formatMoneyWhole(card.delta, currency)} vs {currentYear - 1} YTD
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Overview
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {`Each month has a column per year. Older years are cooler (blue); ${currentYear} is the warmest (red).`}
                    {chartMode === 'cumulative'
                      ? ' Cumulative totals make it easy to see whether this year is ahead or behind at this point.'
                      : ' Monthly totals compare the same calendar month across years.'}
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Income, Expenses & Profit / Loss
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Each month has Income (blue, up), Expenses (orange, down), and Profit (green, up or
                        down) for every year, oldest year first. Profit can go below zero.
                      </Typography>
                      {stackedPlHasAmount(stackedPlRows, yearsWithData) ? (
                        <PerformanceStackedPlChart
                          rows={stackedPlRows}
                          years={yearsWithData}
                          currency={currency}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No income or expenses in this range.
                        </Typography>
                      )}
                    </Box>
                    {overviewCharts.map((chart) => (
                      <Box key={chart.key} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          {chart.title}
                        </Typography>
                        {hasAnyAmount(chart.rows, yearsWithData) ? (
                          <PerformanceSeriesChart
                            rows={chart.rows}
                            years={yearsWithData}
                            currentYear={currentYear}
                            currency={currency}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No {chart.title.toLowerCase()} in this range.
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>

                {sections.map((section) => {
                  const sectionPoints = pointsForSection(points, section.key);
                  const amountMode = SECTION_AMOUNT_MODE[section.key] || 'gross';
                  const sectionRows = buildYearMonthRows(sectionPoints, yearsWithData, {
                    mode: chartMode,
                    amountMode,
                    throughMonth: currentMonth,
                    currentYear,
                  });
                  const categories = categoriesInSection(sectionPoints, section.key);
                  return (
                    <Accordion
                      key={section.key}
                      defaultExpanded={hasAnyAmount(sectionRows, yearsWithData)}
                      disableGutters
                      sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {section.title}
                          {amountMode === 'signed' ? ' · net' : ''}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {!hasAnyAmount(sectionRows, yearsWithData) ? (
                          <Typography variant="body2" color="text.secondary">
                            No {section.title.toLowerCase()} in this range.
                          </Typography>
                        ) : (
                          <Stack spacing={2}>
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                {section.title} total
                              </Typography>
                              <PerformanceSeriesChart
                                rows={sectionRows}
                                years={yearsWithData}
                                currentYear={currentYear}
                                currency={currency}
                              />
                            </Box>
                            {categories.map((category) => {
                              const catRows = buildYearMonthRows(
                                sectionPoints.filter((point) => point.category === category),
                                yearsWithData,
                                {
                                  mode: chartMode,
                                  amountMode,
                                  throughMonth: currentMonth,
                                  currentYear,
                                }
                              );
                              if (!hasAnyAmount(catRows, yearsWithData)) return null;
                              return (
                                <Box key={`${section.key}-${category}`}>
                                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    {category}
                                  </Typography>
                                  <PerformanceSeriesChart
                                    rows={catRows}
                                    years={yearsWithData}
                                    currentYear={currentYear}
                                    currency={currency}
                                    height={240}
                                  />
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default Performance;
